import { useState } from 'react';

interface Example {
  problem: string;
  steps: string[];
}

interface PracticeQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

interface LessonCardProps {
  title: string;
  vocabulary: { term: string; definition: string }[];
  conceptExplanation: string;
  examples: Example[];
  practiceQuestions: PracticeQuestion[];
  teacherTip: string;
}

export default function LessonCard({
  title,
  vocabulary,
  conceptExplanation,
  examples,
  practiceQuestions,
  teacherTip
}: LessonCardProps) {
  const [currentSteps, setCurrentSteps] = useState<number[]>(examples.map(() => 0));
  const [selectedAnswers, setSelectedAnswers] = useState<(number | null)[]>(
    practiceQuestions.map(() => null)
  );
  const [showResults, setShowResults] = useState(false);

  const revealNextStep = (exampleIndex: number) => {
    setCurrentSteps(prev => {
      const newSteps = [...prev];
      if (newSteps[exampleIndex] < examples[exampleIndex].steps.length) {
        newSteps[exampleIndex]++;
      }
      return newSteps;
    });
  };

  const resetExample = (exampleIndex: number) => {
    setCurrentSteps(prev => {
      const newSteps = [...prev];
      newSteps[exampleIndex] = 0;
      return newSteps;
    });
  };

  const handleAnswerSelect = (questionIndex: number, answerIndex: number) => {
    if (!showResults) {
      setSelectedAnswers(prev => {
        const newAnswers = [...prev];
        newAnswers[questionIndex] = answerIndex;
        return newAnswers;
      });
    }
  };

  const checkAnswers = () => {
    setShowResults(true);
  };

  const resetPractice = () => {
    setSelectedAnswers(practiceQuestions.map(() => null));
    setShowResults(false);
  };

  const score = selectedAnswers.filter(
    (answer, index) => answer === practiceQuestions[index].correctAnswer
  ).length;

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
      <h3 className="text-3xl font-bold text-gray-900 mb-6">{title}</h3>

      {/* Vocabulary */}
      <div className="mb-8">
        <h4 className="text-xl font-bold text-indigo-600 mb-4 flex items-center gap-2">
          <i className="ri-book-open-line"></i>
          Key Vocabulary
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {vocabulary.map((item, index) => (
            <div key={index} className="bg-indigo-50 rounded-lg p-4">
              <span className="font-bold text-indigo-700">{item.term}:</span>{' '}
              <span className="text-gray-700">{item.definition}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Concept Explanation */}
      <div className="mb-8">
        <h4 className="text-xl font-bold text-indigo-600 mb-4 flex items-center gap-2">
          <i className="ri-lightbulb-line"></i>
          Understanding the Concept
        </h4>
        <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg p-6">
          <p className="text-gray-800 leading-relaxed whitespace-pre-line">{conceptExplanation}</p>
        </div>
      </div>

      {/* Worked Examples */}
      <div className="mb-8">
        <h4 className="text-xl font-bold text-indigo-600 mb-4 flex items-center gap-2">
          <i className="ri-pencil-ruler-2-line"></i>
          Worked Examples
        </h4>
        <div className="space-y-6">
          {examples.map((example, exampleIndex) => (
            <div key={exampleIndex} className="bg-gray-50 rounded-lg p-6">
              <p className="font-bold text-lg text-gray-900 mb-4">{example.problem}</p>
              <div className="space-y-3 mb-4">
                {example.steps.slice(0, currentSteps[exampleIndex]).map((step, stepIndex) => (
                  <div
                    key={stepIndex}
                    className="bg-white rounded-lg p-4 border-l-4 border-indigo-500 animate-fadeIn"
                  >
                    <p className="text-gray-800">{step}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                {currentSteps[exampleIndex] < example.steps.length && (
                  <button
                    onClick={() => revealNextStep(exampleIndex)}
                    className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Reveal Next Step
                  </button>
                )}
                {currentSteps[exampleIndex] > 0 && (
                  <button
                    onClick={() => resetExample(exampleIndex)}
                    className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg font-semibold hover:bg-gray-400 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Practice Questions */}
      <div className="mb-8">
        <h4 className="text-xl font-bold text-indigo-600 mb-4 flex items-center gap-2">
          <i className="ri-question-line"></i>
          Practice Questions
        </h4>
        <div className="space-y-6">
          {practiceQuestions.map((question, qIndex) => (
            <div key={qIndex} className="bg-indigo-50 rounded-lg p-6">
              <p className="font-bold text-gray-900 mb-4">
                {qIndex + 1}. {question.question}
              </p>
              <div className="space-y-2 mb-4">
                {question.options.map((option, oIndex) => {
                  const isSelected = selectedAnswers[qIndex] === oIndex;
                  const isCorrect = oIndex === question.correctAnswer;
                  const showCorrect = showResults && isCorrect;
                  const showIncorrect = showResults && isSelected && !isCorrect;

                  return (
                    <button
                      key={oIndex}
                      onClick={() => handleAnswerSelect(qIndex, oIndex)}
                      disabled={showResults}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-300 whitespace-nowrap cursor-pointer ${
                        showCorrect
                          ? 'border-green-500 bg-green-50'
                          : showIncorrect
                          ? 'border-red-500 bg-red-50'
                          : isSelected
                          ? 'border-indigo-500 bg-indigo-100'
                          : 'border-gray-300 bg-white hover:border-indigo-300'
                      }`}
                    >
                      <span className="font-semibold">{String.fromCharCode(65 + oIndex)}.</span> {option}
                      {showCorrect && (
                        <i className="ri-check-line text-green-600 float-right text-xl"></i>
                      )}
                      {showIncorrect && (
                        <i className="ri-close-line text-red-600 float-right text-xl"></i>
                      )}
                    </button>
                  );
                })}
              </div>
              {showResults && (
                <div className="bg-white rounded-lg p-4 border-l-4 border-indigo-500">
                  <p className="text-gray-800">
                    <strong>Explanation:</strong> {question.explanation}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-4 mt-6">
          {!showResults ? (
            <button
              onClick={checkAnswers}
              disabled={selectedAnswers.some(answer => answer === null)}
              className="bg-indigo-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-indigo-700 transition-colors duration-300 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              Check My Answers
            </button>
          ) : (
            <>
              <div className="bg-indigo-100 px-6 py-3 rounded-lg">
                <span className="text-2xl font-bold text-indigo-700">
                  Score: {score}/{practiceQuestions.length}
                </span>
              </div>
              <button
                onClick={resetPractice}
                className="bg-indigo-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-indigo-700 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </div>

      {/* Teacher's Tip */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-6 border-l-4 border-amber-500">
        <h4 className="text-lg font-bold text-amber-700 mb-2 flex items-center gap-2">
          <i className="ri-user-star-line"></i>
          Teacher's Tip from Mr. Augustine
        </h4>
        <p className="text-gray-800 leading-relaxed">{teacherTip}</p>
      </div>
    </div>
  );
}